
import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/layout/AdminLayout';
import { ApplicationList } from '@/components/admin/application/ApplicationList';
import { DeleteApplicationDialog } from '@/components/admin/application/DeleteApplicationDialog';
import { ResetApplicationDialog } from '@/components/admin/application/ResetApplicationDialog';
import { ApplicationViewSheet } from '@/components/admin/application/ApplicationViewSheet';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Application } from '@/types/database.types';
import { Json } from '@/integrations/supabase/types';
import { processAdditionalContacts, processPayrollSchedules } from '@/utils/application-data-utils';

const AdminApplicationsPage = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [isViewSheetOpen, setIsViewSheetOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Fetch application data
  const fetchApplications = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setApplications(data as unknown as Application[]);
    } catch (error: any) {
      console.error('Error fetching applications:', error);
      toast({
        title: 'Error loading applications',
        description: error.message || 'Failed to load applications',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Initial data fetch
  useEffect(() => {
    fetchApplications();
  }, []);
  
  // Handler functions
  const handleViewApplication = (id: string) => {
    setSelectedAppId(id);
  };
  
  const handleResetRequest = (id: string) => {
    setSelectedAppId(id);
    setIsResetDialogOpen(true);
  };
  
  const handleDeleteRequest = (id: string) => {
    setSelectedAppId(id);
    setIsDeleteDialogOpen(true);
  };
  
  const handleResetApplication = async () => {
    if (!selectedAppId) return;
    
    setIsActionInProgress(true);
    try {
      // Reset application logic
      await supabase
        .from('applications')
        .update({ 
          status: 'draft',
          progress: 0 
        })
        .eq('id', selectedAppId);
      
      toast({
        title: 'Application reset',
        description: 'The application has been reset to draft status.',
      });
      fetchApplications();
    } catch (error: any) {
      toast({
        title: 'Error resetting application',
        description: error.message || 'Failed to reset application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
      setIsResetDialogOpen(false);
    }
  };
  
  const handleDeleteApplication = async () => {
    if (!selectedAppId) return;
    
    setIsActionInProgress(true);
    setDeleteErrorMessage(null);
    try {
      // Delete application logic
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', selectedAppId);
      
      if (error) throw error;
      
      toast({
        title: 'Application deleted',
        description: 'The application has been permanently deleted.',
      });
      fetchApplications();
    } catch (error: any) {
      setDeleteErrorMessage(error.message || 'Failed to delete application');
      toast({
        title: 'Error deleting application',
        description: error.message || 'Failed to delete application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
      setIsDeleteDialogOpen(false);
    }
  };
  
  const handleSubmitApplication = async (id: string) => {
    setIsActionInProgress(true);
    try {
      // Submit application logic
      await supabase
        .from('applications')
        .update({ 
          status: 'submitted' 
        })
        .eq('id', id);
      
      toast({
        title: 'Application submitted',
        description: 'The application has been successfully submitted.',
      });
      fetchApplications();
    } catch (error: any) {
      toast({
        title: 'Error submitting application',
        description: error.message || 'Failed to submit application',
        variant: 'destructive',
      });
    } finally {
      setIsActionInProgress(false);
    }
  };
  
  const handleApplicationUpdate = async (id: string, data: Partial<Application>) => {
    try {
      // Convert the data to a format that Supabase accepts
      // Handle array fields properly
      const updateData: Record<string, any> = { ...data };
      
      // Properly handle additional_contacts and additional_schedules
      if (Array.isArray(updateData.additional_contacts)) {
        updateData.additional_contacts = updateData.additional_contacts as unknown as Json;
      }
      
      if (Array.isArray(updateData.additional_schedules)) {
        updateData.additional_schedules = updateData.additional_schedules as unknown as Json[];
      }
      
      // Convert arrays to strings for these fields if they exist
      const arrayFields = [
        'desired_medical_carriers',
        'desired_ancillary_carriers',
        'other_documents',
        'states_of_operation',
        'states_with_employees',
        'top_3_peo_needs',
        'preferred_peos',
        'excluded_peos'
      ];
      
      arrayFields.forEach(field => {
        if (Array.isArray(updateData[field])) {
          updateData[field] = JSON.stringify(updateData[field]);
        }
      });
      
      await supabase
        .from('applications')
        .update(updateData)
        .eq('id', id);
      
      fetchApplications();
      return true;
    } catch (error) {
      console.error('Error updating application:', error);
      return false;
    }
  };

  return (
    <AdminLayout>
      <ApplicationList
        applications={applications}
        isLoading={isLoading}
        onView={(id) => {
          handleViewApplication(id);
          setIsViewSheetOpen(true);
        }}
        onResetRequest={handleResetRequest}
        onDeleteRequest={handleDeleteRequest}
        onSubmit={handleSubmitApplication}
        isActionInProgress={isActionInProgress}
      />
      
      {/* Application View Sheet */}
      <ApplicationViewSheet 
        applicationId={selectedAppId}
        isOpen={isViewSheetOpen}
        onOpenChange={setIsViewSheetOpen}
      />
      
      {/* Reset Confirmation Dialog */}
      <ResetApplicationDialog
        isOpen={isResetDialogOpen}
        onOpenChange={setIsResetDialogOpen}
        onConfirm={handleResetApplication}
        isActionInProgress={isActionInProgress}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteApplicationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteApplication}
        isActionInProgress={isActionInProgress}
        errorMessage={deleteErrorMessage}
      />
    </AdminLayout>
  );
};

export default AdminApplicationsPage;
